import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import Button from './Button';
import Modal from './Modal';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ItemPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => `${theme.colors.primary}10`};
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
  }

  div {
    h4 {
      margin: 0;
      font-size: 1rem;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
      color: ${({ theme }) => theme.colors.text};
    }
  }
`;

const LocationList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const RoomSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const RoomHeader = styled.div`
  font-weight: 900;
  padding: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.primary};
  border-bottom: 1px solid #eee;
`;

const BinList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
`;

const BinOption = styled.button`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ selected, theme }) => selected ? theme.colors.primary : '#ddd'};
  border-radius: 4px;
  background: ${({ selected, theme }) => selected ? `${theme.colors.primary}10` : theme.colors.white};
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}10`};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f5f5f5;
  }
`;

const BinNumber = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const BinName = styled.span`
  flex: 1;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const MoveItemModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  item, 
  currentBin,
  availableRooms 
}) => {
  const [selectedRoom, setSelectedRoom] = useState('');
  const [availableBins, setAvailableBins] = useState([]);
  const [selectedBin, setSelectedBin] = useState('');
  const [roomBins, setRoomBins] = useState({});

  useEffect(() => {
    const fetchAllBins = async () => {
      try {
        const binsData = {};
        for (const room of availableRooms) {
          const binsQuery = query(
            collection(db, 'bins'),
            where('roomId', '==', room.id)
          );
          const binsSnapshot = await getDocs(binsQuery);
          binsData[room.id] = binsSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .sort((a, b) => a.number - b.number);
        }
        setRoomBins(binsData);
      } catch (error) {
        console.error('Error fetching bins:', error);
      }
    };

    if (availableRooms?.length > 0) {
      fetchAllBins();
    }
  }, [availableRooms]);

  const handleBinSelect = (roomId, binId) => {
    setSelectedRoom(roomId);
    setSelectedBin(binId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedRoom || !selectedBin) return;
    onSubmit(selectedRoom, selectedBin);
  };

  if (!item) return null;

  const isCurrentBin = (binId) => item.binId === binId;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Move Item"
    >
      <Form onSubmit={handleSubmit}>
        <ItemPreview>
          {item.photoUrl && <img src={item.photoUrl} alt={item.name} />}
          <div>
            <h4>{item.name}</h4>
            <p>Currently in: {currentBin?.name || `Bin ${currentBin?.number}`}</p>
          </div>
        </ItemPreview>

        <LocationList>
          {availableRooms
            .sort((a, b) => {
              if (a.id === item.roomId) return -1;
              if (b.id === item.roomId) return 1;
              return a.name.localeCompare(b.name);
            })
            .map(room => (
              <RoomSection key={room.id}>
                <RoomHeader>
                  {room.name}
                  {room.id === item.roomId && " (Current Room)"}
                </RoomHeader>
                <BinList>
                  <BinOption
                    key={`${room.id}-room-items`}
                    type="button"
                    selected={selectedRoom === room.id && selectedBin === '0'}
                    disabled={item.roomId === room.id && (!item.binId || item.binId === '0')}
                    onClick={() => handleBinSelect(room.id, '0')}
                  >
                    <BinNumber>Room Items</BinNumber>
                    <BinName></BinName>
                    {(!item.binId || item.binId === '0') && item.roomId === room.id && " (Current)"}
                  </BinOption>
                  {roomBins[room.id]?.filter(bin => bin.number !== 0).map(bin => (
                    <BinOption
                      key={bin.id}
                      type="button"
                      selected={selectedRoom === room.id && selectedBin === bin.id}
                      disabled={isCurrentBin(bin.id)}
                      onClick={() => handleBinSelect(room.id, bin.id)}
                    >
                      <BinNumber>Bin {bin.number}</BinNumber>
                      <BinName>{bin.name}</BinName>
                      {isCurrentBin(bin.id) && " (Current)"}
                    </BinOption>
                  ))}
                </BinList>
              </RoomSection>
            ))}
        </LocationList>

        <ButtonContainer>
          <Button 
            type="button" 
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={!selectedRoom || !selectedBin}
          >
            Move Item
          </Button>
        </ButtonContainer>
      </Form>
    </Modal>
  );
};

export default MoveItemModal; 